import React from 'react'
import { Group, Rect, Text, Circle, RegularPolygon } from 'react-konva'
import type { PreviewSnapshot, CarMeta } from '@nyos/shared'
import {
  projectToCanvas,
  applyOrientationTransform,
  type CanvasOffset,
  type MapViewState,
} from '../utils/yardMapUtils'
import type { GisLayoutOrientation } from '../types/interchange'

/**
 * Animated switch-list preview markers (Plan 26 Phase 1d): the locomotive
 * plus every car in the cast, drawn at the playback snapshot's lon/lat.
 * Parked cars render dimmer; the coupled consist renders full-strength.
 * Render-only — geometry comes from shared/animation (pure, Vitest-covered).
 */

const LOCO_LENGTH_FT = 74
const DEFAULT_CAR_FT = 50
/** Legibility floor for far-out zooms — never used to space cars. */
const MIN_W = 4
const FT_PER_DEG_LAT = 364320 // ~69 statute miles per degree of latitude

interface PreviewConsistLayerProps {
  snapshot: PreviewSnapshot | null
  carLengths?: Map<string, number>
  /** Display metadata — reporting marks + hazmat classes (labels/badges). */
  carMeta?: Map<string, CarMeta>
  /** All-approx inbound flow: hide per-car dots, the modal shows one note. */
  suppressApproxDots?: boolean
  cameraState: MapViewState
  canvasOffset: CanvasOffset
  stageSize: { width: number; height: number }
  yardOrientation: GisLayoutOrientation
  themeConfig: Record<string, string>
}

const PreviewConsistLayer: React.FC<PreviewConsistLayerProps> = ({
  snapshot,
  carLengths,
  cameraState,
  carMeta,
  suppressApproxDots,
  canvasOffset,
  stageSize,
  yardOrientation,
  themeConfig,
}) => {
  if (!snapshot) return null

  const toXY = (lon: number, lat: number) => {
    const p = projectToCanvas(lon, lat, cameraState, canvasOffset, stageSize.width, stageSize.height)
    return applyOrientationTransform(p.x, p.y, yardOrientation, stageSize.width)
  }
  const toAngle = (angleDeg: number) =>
    yardOrientation === 'REVERSED' ? 180 - angleDeg : angleDeg

  // True-to-scale sizing: project a small longitude delta at the consist's
  // latitude and convert each car's real lengthFeet to canvas px, so
  // consist spacing matches the sim's feet offsets at every zoom level.
  const refLon = snapshot.loco?.lon ?? snapshot.cars[0]?.pos.lon ?? 0
  const refLat = snapshot.loco?.lat ?? snapshot.cars[0]?.pos.lat ?? 0
  const DEG = 0.0005
  const probe0 = toXY(refLon, refLat)
  const probe1 = toXY(refLon + DEG, refLat)
  const feetPerDegLon = FT_PER_DEG_LAT * Math.cos((refLat * Math.PI) / 180)
  const pxPerFoot =
    feetPerDegLon > 0
      ? Math.hypot(probe1.x - probe0.x, probe1.y - probe0.y) / (DEG * feetPerDegLon)
      : 0.2
  const widthFor = (lengthFeet: number) => Math.max(MIN_W, lengthFeet * pxPerFoot)

  return (
    <>
      {/* F-UI-1 ambient bodies — parked inventory that never joined the
          consist. Drawn dimmed UNDER the consist so a blocked leg shows
          the standing cars it physically cannot pass (e.g. the blocker
          named in a TRANSIT_OBSTRUCTED reason). Marks render when zoom
          allows — the banner names the same cars. */}
      {(snapshot.ambientCars ?? []).map((car) => {
        const { x, y } = toXY(car.pos.lon, car.pos.lat)
        const w = widthFor(carLengths?.get(car.carId) ?? DEFAULT_CAR_FT)
        const h = Math.max(3, w * 0.4)
        const rx = car.bodyDir === 'forward' ? 0 : -w
        const meta = carMeta?.get(car.carId)
        const label = meta?.reportingMark
        const rotation = toAngle(car.pos.angleDeg)
        return (
          <Group key={`ambient-${car.carId}`} x={x} y={y} rotation={rotation} opacity={0.28}>
            <Rect
              x={rx}
              y={-h / 2}
              width={w}
              height={h}
              fill={themeConfig.carBg}
              stroke={themeConfig.railcarStroke}
              strokeWidth={1}
              dash={[3, 3]}
              cornerRadius={3}
            />
            {label && w >= 30 && (
              <Text
                text={label}
                x={rx + w / 2}
                y={-h / 2 - (w >= 44 ? 13 : 11)}
                fontSize={w >= 44 ? 10 : 8.5}
                fill={themeConfig.textPrimary}
                offsetX={label.length * 3}
                rotation={-rotation}
                listening={false}
              />
            )}
          </Group>
        )
      })}

      {snapshot.cars.map((car) => {
        const { x, y } = toXY(car.pos.lon, car.pos.lat)
        const w = widthFor(carLengths?.get(car.carId) ?? DEFAULT_CAR_FT)
        const h = Math.max(3, w * 0.4)
        // 'trail': pos is the head, body behind it (x=-w).
        // 'forward': pos is the A-end edge, body toward +A→B (x=0).
        const rx = car.bodyDir === 'forward' ? 0 : -w
        const meta = carMeta?.get(car.carId)
        const isHaz = !!(meta?.hazardous || meta?.hazmatClass)
        const label = meta?.reportingMark
          ? `${meta.reportingMark}${meta?.hazmatClass ? ` ⚠${meta.hazmatClass}` : ''}`
          : undefined
        const rotation = toAngle(car.pos.angleDeg)
        const leadX = rx === 0 ? w : 0 // body-attached lead end
        return (
          <Group
            key={car.carId}
            x={x}
            y={y}
            rotation={rotation}
            opacity={car.inConsist ? 0.95 : 0.45}
          >
            <Rect
              x={rx}
              y={-h / 2}
              width={w}
              height={h}
              fill={themeConfig.carBg}
              stroke={themeConfig.railcarStroke}
              strokeWidth={1.5}
              dash={car.approx ? [4, 3] : undefined}
              cornerRadius={3}
            />
            {car.approx && !suppressApproxDots && (
              <Circle radius={3} fill={themeConfig.switchDiverged} x={leadX} y={-h / 2} listening={false} />
            )}
            {/* Hazmat placard — amber diamond at the lead end. */}
            {isHaz && w >= 18 && (
              <RegularPolygon
                x={leadX}
                y={0}
                sides={4}
                radius={w >= 24 ? 4.5 : 3.5}
                fill="#f59e0b"
                stroke="#7c2d12"
                strokeWidth={1}
                listening={false}
              />
            )}
            {/* Reporting-mark label — horizontal, above the body. */}
            {label && w >= 30 && (
              <Text
                text={label}
                x={rx + w / 2}
                y={-h / 2 - (w >= 44 ? 13 : 11)}
                fontSize={w >= 44 ? 10 : 8.5}
                fontStyle="bold"
                fill={themeConfig.textPrimary}
                offsetX={label.length * 3}
                rotation={-rotation}
                listening={false}
              />
            )}
          </Group>
        )
      })}

      {snapshot.loco && (() => {
        const { x, y } = toXY(snapshot.loco.lon, snapshot.loco.lat)
        const w = widthFor(LOCO_LENGTH_FT)
        const h = Math.max(3, w * 0.42)
        const macro = w < 30 // label only when the loco is big enough to read
        return (
          <Group x={x} y={y} rotation={toAngle(snapshot.loco.angleDeg)}>
            <Rect
              x={-w}
              y={-h / 2}
              width={w}
              height={h}
              fill={themeConfig.switchDiverged}
              stroke={themeConfig.railcarStroke}
              strokeWidth={2}
              cornerRadius={4}
              shadowBlur={8}
              shadowColor={themeConfig.switchDiverged}
              shadowOpacity={0.6}
            />
            {!macro && (
              <Text
                text="LOCO"
                x={0}
                y={-h / 2 - 14}
                fill={themeConfig.textPrimary}
                fontSize={11}
                fontStyle="bold"
                offsetX={11}
                rotation={-toAngle(snapshot.loco.angleDeg)}
                listening={false}
              />
            )}
          </Group>
        )
      })()}
    </>
  )
}

export default React.memo(PreviewConsistLayer)
